import { CUSTOM_METRIC_PREFIX, FixedInfoMetric, InfoMetric } from 'src/components/dispatch/unassignedDisplay/displayMetricKeys';

export function extractPrefecture(address: string | undefined | null): string {
  if (!address) return '';
  const match = address.match(/^〒?\d{3}-?\d{4}\s*(.+?[都道府県])/);
  if (match) return match[1];
  const simple = address.match(/(.+?[都道府県])/);
  return simple ? simple[1] : '';
}

export function extractCity(address: string | undefined | null): string {
  if (!address) return '';
  const match = address.match(/[都道府県](.+?[市区町村])/);
  return match ? match[1] : '';
}

export function formatTimeWindow(tw: string | undefined | null): string {
  if (!tw) return '';
  const times = tw.match(/(\d{1,2}:\d{2})/g);
  if (times && times.length >= 2) return `${times[0]}-${times[times.length - 1]}`;
  if (times && times.length === 1) return times[0];
  return tw;
}

interface CardLikeData {
  origin?: string;
  destination?: string;
  storeName?: string;
  storeAddress?: string;
  originAddress?: string;
  shippers?: string[];
  originTimeWindow?: string;
  destTimeWindow?: string;
  constraints?: string[];
  /** Backend (Google Geocoder) で正規化済みの積地都道府県 */
  loadingPrefecture?: string | null;
  /** Backend (Google Geocoder) で正規化済みの積地市区町村 */
  loadingCity?: string | null;
  /** Backend (Google Geocoder) で正規化済みの降地都道府県 */
  unloadingPrefecture?: string | null;
  /** Backend (Google Geocoder) で正規化済みの降地市区町村 */
  unloadingCity?: string | null;
  /** 会社設定の任意項目の値。未割当カードは source.custom_input_fields から詰める */
  customFields?: { key: string; value: string }[];
  /** 伝票No。未割当カードは source.code から詰める（invoices は未設定のため使えない） */
  invoiceNo?: string;
}

const ORIGIN_PREF_HINTS: { match: RegExp; pref: string }[] = [
  { match: /新宿|渋谷|品川|大手町|池袋|東京|目白|目黒|世田谷|港区|千代田|中央区/, pref: '東京都' },
  { match: /横浜|川崎|神奈川|湘南/, pref: '神奈川県' },
  { match: /大阪|梅田|難波|心斎橋/, pref: '大阪府' },
  { match: /名古屋|愛知/, pref: '愛知県' },
  { match: /埼玉|大宮|川口|浦和/, pref: '埼玉県' },
  { match: /千葉|船橋|柏/, pref: '千葉県' },
];

function guessPrefectureFromLabel(label: string | undefined | null): string {
  if (!label) return '';
  const direct = extractPrefecture(label);
  if (direct) return direct;
  for (const { match, pref } of ORIGIN_PREF_HINTS) {
    if (match.test(label)) return pref;
  }
  return '';
}

function guessCityFromLabel(label: string | undefined | null): string {
  if (!label) return '';
  const direct = extractCity(label);
  if (direct) return direct;
  const trimmed = label.replace(/センター|サブセンター|営業所|倉庫|デポ/g, '').trim();
  return trimmed || '';
}

export function getInfoMetricValue(metric: InfoMetric, data: CardLikeData): string {
  // custom: は動的キーなので switch では網羅できない。先に処理する。
  if (metric.startsWith(CUSTOM_METRIC_PREFIX)) {
    const name = metric.slice(CUSTOM_METRIC_PREFIX.length);
    return (data.customFields ?? []).find((f) => f.key === name)?.value || '';
  }

  switch (metric as FixedInfoMetric) {
    case 'origin':
      return data.origin || '';
    case 'destination':
      return data.destination || data.storeName || '';
    case 'section_pref': {
      // 正規化済み (Backend Google Geocoder) を優先、なければ regex/ヒューリスティック fallback
      const fromPref = data.loadingPrefecture
        || guessPrefectureFromLabel(data.originAddress)
        || guessPrefectureFromLabel(data.origin);
      const toPref = data.unloadingPrefecture
        || extractPrefecture(data.storeAddress)
        || guessPrefectureFromLabel(data.destination);
      if (fromPref && toPref) return `${fromPref}→${toPref}`;
      return fromPref || toPref || '';
    }
    case 'section_city': {
      // 正規化済み (Backend Google Geocoder) を優先、なければ regex/ヒューリスティック fallback
      const fromCity = data.loadingCity
        || guessCityFromLabel(data.originAddress)
        || guessCityFromLabel(data.origin);
      const toCity = data.unloadingCity
        || extractCity(data.storeAddress)
        || guessCityFromLabel(data.destination);
      if (fromCity && toCity) return `${fromCity}→${toCity}`;
      return fromCity || toCity || '';
    }
    case 'origin_address':
      return data.originAddress || '';
    case 'dest_address':
      return data.storeAddress || '';
    case 'shipper':
      return data.shippers?.join(', ') || '';
    case 'time_window': {
      const origin = formatTimeWindow(data.originTimeWindow);
      const dest = formatTimeWindow(data.destTimeWindow);
      if (origin && dest) return `${origin} → ${dest}`;
      return origin || dest || '';
    }
    case 'conditions':
      return data.constraints?.join(', ') || '';
    case 'invoice':
      return data.invoiceNo || '';
  }
  // InfoMetric に custom:${string} が含まれるため、switch だけでは戻り値が確定しない
  return '';
}
